import React, { useState } from "react";
import api, { API, apiErr } from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import { ClientSearchSelect } from "../components/ClientSearchSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileText, Download, HardDrive, Receipt, HandCoins, AlertTriangle, Clock } from "lucide-react";

const CATEGORIES = ["Harç", "Tebligat", "Bilirkişi", "Keşif", "Noter", "Tercüme", "TÜRKPATENT", "Ulaşım", "Konaklama", "Kargo", "Vergi", "Resmî Kurum Ödemesi", "Diğer"];
const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

const REPORTS = [
  { key: "expenses", label: "Masraf Raporu", icon: Receipt, path: "/reports/expenses" },
  { key: "collections", label: "Tahsilat Raporu", icon: HandCoins, path: "/reports/collections" },
  { key: "critical-advances", label: "Kritik Avans", icon: AlertTriangle, path: "/reports/critical-advances" },
  { key: "overdue-fees", label: "Geciken Ödemeler", icon: Clock, path: "/reports/overdue-fees" },
];

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>;
}

export default function Reports() {
  const [active, setActive] = useState("expenses");
  const [client, setClient] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [category, setCategory] = useState("");
  const [currency, setCurrency] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const meta = REPORTS.find((r) => r.key === active);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (active === "expenses" || active === "collections") {
      if (start) p.set("start", start);
      if (end) p.set("end", end);
      if (client) p.set("client_id", client.id);
      if (currency) p.set("currency", currency);
    }
    if (active === "expenses" && category) p.set("category", category);
    if (active === "collections" && type) p.set("type", type);
    if (active === "critical-advances" && status) p.set("status", status);
    return p;
  };

  const run = () => {
    setLoading(true);
    api.get(`${meta.path}?${buildParams().toString()}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))).finally(() => setLoading(false));
  };

  const download = (fmt) => {
    const p = buildParams(); p.set("format", fmt);
    window.open(`${API}${meta.path}?${p.toString()}`, "_blank");
  };

  const saveDrive = async (fmt) => {
    const p = buildParams(); p.set("format", fmt); p.set("save_to_drive", "true");
    try {
      const { data } = await api.get(`${meta.path}?${p.toString()}`);
      if (data.saved && data.drive_link) toast.success("Rapor Google Drive'a kaydedildi");
      else toast.error("Google Drive bağlı değil");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const switchReport = (k) => { setActive(k); setData(null); };

  return (
    <div className="space-y-5 animate-in-up">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">Raporlar</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {REPORTS.map((r) => (
          <button key={r.key} data-testid={`report-tab-${r.key}`} onClick={() => switchReport(r.key)}
            className={`flex items-center gap-2 px-3 py-3 rounded-xl border text-sm font-medium transition-colors ${active === r.key ? "bg-navy-900 text-white border-navy-900" : "bg-white text-navy-900 border-slate-200 hover:bg-slate-50"}`}>
            <r.icon className="w-4 h-4" /> <span className="truncate">{r.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {(active === "expenses" || active === "collections") && (
            <>
              <Fld label="Başlangıç"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl" data-testid="report-start" /></Fld>
              <Fld label="Bitiş"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl" data-testid="report-end" /></Fld>
              <Fld label="Müvekkil (opsiyonel)"><ClientSearchSelect selected={client} onSelect={setClient} testid="report-client-select" /></Fld>
              <Fld label="Para Birimi">
                <Select value={currency || "ALL"} onValueChange={(v) => setCurrency(v === "ALL" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                  <SelectContent><SelectItem value="ALL">Tümü</SelectItem>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            </>
          )}
          {active === "expenses" && (
            <Fld label="Kategori">
              <Select value={category || "ALL"} onValueChange={(v) => setCategory(v === "ALL" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Tümü</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
          )}
          {active === "collections" && (
            <Fld label="Tahsilat Türü">
              <Select value={type || "ALL"} onValueChange={(v) => setType(v === "ALL" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tümü</SelectItem>
                  <SelectItem value="advance">Masraf Avansı</SelectItem>
                  <SelectItem value="fee">Vekâlet Ücreti</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
          )}
          {active === "critical-advances" && (
            <Fld label="Durum">
              <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tümü</SelectItem>
                  <SelectItem value="DUSUK">Düşük</SelectItem>
                  <SelectItem value="AVANS_TUKENDI">Tükendi</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="report-run" onClick={run} disabled={loading} className="rounded-xl bg-navy-900 text-white">{loading ? "Oluşturuluyor…" : "Raporu Oluştur"}</Button>
          <Button variant="outline" className="rounded-xl" data-testid="report-pdf" onClick={() => download("pdf")}><Download className="w-4 h-4 mr-1" />PDF</Button>
          <Button variant="outline" className="rounded-xl" data-testid="report-excel" onClick={() => download("excel")}><Download className="w-4 h-4 mr-1" />Excel</Button>
          <Button variant="outline" className="rounded-xl" data-testid="report-drive" onClick={() => saveDrive("pdf")}><HardDrive className="w-4 h-4 mr-1" />Drive'a Kaydet</Button>
        </div>
      </div>

      {/* Results */}
      {data && <ReportResults active={active} data={data} />}
    </div>
  );
}

function ReportResults({ active, data }) {
  const items = data.items || [];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      {(active === "expenses" || active === "collections") && data.totals && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(data.totals).map(([c, v]) => (
            <span key={c} className="px-3 py-1.5 rounded-xl bg-navy-900 text-white text-sm font-mono">{fmtMoney(v, c)}</span>
          ))}
          {Object.keys(data.totals).length === 0 && <span className="text-sm text-slate-400">Kayıt yok</span>}
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Sonuç yok.</p> : (
        <div className="divide-y divide-slate-100">
          {items.map((i, idx) => (
            <div key={idx} className="flex items-center justify-between py-3 gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy-900 truncate">
                  {active === "expenses" && `${i.category} · ${i.client}`}
                  {active === "collections" && `${i.type} · ${i.client}`}
                  {active === "critical-advances" && `${i.client_no} — ${i.name}`}
                  {active === "overdue-fees" && `${i.client_no} — ${i.name}`}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {active === "expenses" && `${fmtDate(i.date)} · ${i.description || ""}`}
                  {active === "collections" && `${fmtDate(i.date)} · ${i.method || ""}`}
                  {active === "critical-advances" && `Min: ${fmtMoney(i.min_limit)} · Son avans: ${i.last_advance ? fmtDate(i.last_advance) : "—"}`}
                  {active === "overdue-fees" && `Vade ${fmtDate(i.due_date)} · ${i.days_late} gün gecikme`}
                </p>
              </div>
              <div className="text-right shrink-0 font-mono text-sm">
                {active === "expenses" && <span className="text-brand-red">{fmtMoney(i.amount, i.currency)}</span>}
                {active === "collections" && <span className="text-emerald-600">{fmtMoney(i.amount, i.currency)}</span>}
                {active === "critical-advances" && <span className="text-brand-red">Eksik {fmtMoney(i.missing)}</span>}
                {active === "overdue-fees" && <span className="text-brand-red">Kalan {fmtMoney(i.remaining, i.currency)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
